import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Warehouse } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import localService, { type Local, type LocalTipo } from '@/services/local';

const TIPO_LABELS: Record<LocalTipo, string> = {
  ARMAZEM: 'Armazém',
  DEPOSITO: 'Depósito',
  LINHA_PRODUCAO: 'Linha de Produção',
  EXPEDICAO: 'Expedição',
  QUARENTENA: 'Quarentena',
  REFUGO: 'Refugo',
  OUTRO: 'Outro',
};

export default function LocaisPage() {
  const navigate = useNavigate();
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      setLocais(await localService.listar());
    } catch (error) {
      console.error('Erro ao carregar locais', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const locaisFiltrados = useMemo(() => {
    if (!busca) return locais;
    const termo = busca.toLowerCase();
    return locais.filter((l) => l.codigo.toLowerCase().includes(termo) || l.descricao.toLowerCase().includes(termo));
  }, [locais, busca]);

  const deletar = async (item: Local) => {
    try {
      await localService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir local', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Warehouse size={21} strokeWidth={2} />}
        title="Locais de Armazenamento"
        description="Cadastro de locais de armazenamento utilizados na industrialização."
        actions={
          <Button type="button" onClick={() => navigate('/producao/locais/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Local
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar código ou descrição..."
                />
              </div>
            </div>
          }
        />

        {!loading && locaisFiltrados.length === 0 ? (
          <EmptyState message="Nenhum local encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Tipo</th>
                  <th>Capacidade</th>
                  <th>Ativo</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  locaisFiltrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.codigo}</td>
                      <td>{item.descricao}</td>
                      <td>{TIPO_LABELS[item.tipo] || item.tipo}</td>
                      <td>{item.capacidade ? `${item.capacidade} ${item.unidade_capacidade || ''}` : '-'}</td>
                      <td>
                        <StatusBadge label={item.ativo ? 'Sim' : 'Não'} tone={item.ativo ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <TableActions
                          onEdit={() => navigate(`/producao/locais/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          deleteTitle="Excluir local"
                          deleteDescription={`Tem certeza de que deseja excluir o local "${item.descricao}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
